import { useMemo } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { getTooltipId, formatNumber } from '../../../../utils/utils';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import { Tooltip } from 'react-tooltip';
import { getComparableCompaniesGridTemplateColumns } from './comparableCompaniesGrid';
import { openInNewTabOnModifier } from '../../../../utils/openInNewTab';

/** Watchlist button tooltip copy for access / add / remove states. */
const getWatchlistTooltipContent = (canViewWatchlist, isInWatchlist) => {
  if (!canViewWatchlist) {
    return (
      <>
        Requires Profiler Lite or Profiler Full access.
        <br />
        Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
        assistance.
      </>
    );
  }
  return isInWatchlist ? <>Remove from watchlist</> : <>Add to watchlist</>;
};

/** Watchlist tooltip placement for desktop / mobile / locked access. */
const getWatchlistTooltipPosition = (canViewWatchlist, layout, isInWatchlist) => {
  if (!canViewWatchlist) return 'bottom-end';
  if (layout === 'desktop') return 'bottom-center';
  return isInWatchlist ? 'bottom-end' : 'bottom-center';
};

/** Desktop grid style for a comparable company row (empty object on mobile). */
const getComparableCompanyItemStyle = (layout, hasExtendedAccess, hasDetailsAccess) => {
  if (layout !== 'desktop') return {};
  return {
    gridTemplateColumns: getComparableCompaniesGridTemplateColumns(
      hasExtendedAccess,
      hasDetailsAccess
    )
  };
};

/** Sales cell value with optional gross-profit / estimate tooltips. */
const ComparableCompanyRevenueValue = ({
  company,
  grossProfitTooltipId,
  basicRevenueTooltipId
}) => {
  if (!company.revenue) return PUNCTUATION.EMPTY_VALUE;

  const basicStyle = company.isRevenueBasic ? { color: '#348cf9' } : {};
  return (
    <>
      {!!company.isRevenueGrossProfit && (
        <>
          <div id={grossProfitTooltipId} className="default-black-info-circle-wrap">
            <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
          </div>
          <Tooltip
            anchorSelect={`#${grossProfitTooltipId}`}
            multiline={true}
            variant="dark"
            place="bottom-center"
            opacity={1}
            content={
              <>
                This company was included
                <br />
                based on reported gross profit,
                <br />
                not revenue.
              </>
            }
            className={'default-black-info-tooltip-classname'}
          />
        </>
      )}
      <font id={basicRevenueTooltipId} style={basicStyle}>
        {company.revenue}
      </font>
      <span style={basicStyle} className="year">
        &lsquo;
        {company.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
      </span>
      {!!company.isRevenueBasic && (
        <Tooltip
          anchorSelect={`#${basicRevenueTooltipId}`}
          multiline={true}
          variant="dark"
          place="bottom-center"
          opacity={1}
          content={<>Based on self-disclosure or estimate</>}
        />
      )}
    </>
  );
};

ComparableCompanyRevenueValue.propTypes = {
  company: PropTypes.object.isRequired,
  grossProfitTooltipId: PropTypes.string.isRequired,
  basicRevenueTooltipId: PropTypes.string.isRequired
};

/** Employees cell value with optional estimate tooltip. */
const ComparableCompanyEmployeesValue = ({ company, basicEmployeesTooltipId }) => {
  if (Number.isNaN(Number(company.employees))) return PUNCTUATION.EMPTY_VALUE;

  const basicStyle = company.isEmployeesBasic ? { color: '#348cf9' } : {};
  return (
    <>
      <font id={basicEmployeesTooltipId} style={basicStyle}>
        {formatNumber((+company.employees).toFixed(2))}
      </font>
      <span className="year" style={basicStyle}>
        &lsquo;
        {company.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
      </span>
      {!!company.isEmployeesBasic && (
        <Tooltip
          anchorSelect={`#${basicEmployeesTooltipId}`}
          multiline={true}
          variant="dark"
          place="bottom-center"
          opacity={1}
          content={<>Based on self-disclosure or estimate</>}
        />
      )}
    </>
  );
};

ComparableCompanyEmployeesValue.propTypes = {
  company: PropTypes.object.isRequired,
  basicEmployeesTooltipId: PropTypes.string.isRequired
};

/** EBIT + EBIT-margin columns (Full / extended access only). */
const ComparableCompanyExtendedFinancialCols = ({ company }) => (
  <>
    <div className="col ebit">
      <div className="label">EBIT (€M)</div>
      <div className="value">
        {company.ebit ? (
          <>
            {company.ebit}
            <span className="year">
              &lsquo;
              {company.ebitYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
            </span>
          </>
        ) : (
          PUNCTUATION.EMPTY_VALUE
        )}
      </div>
    </div>
    <div className="col ebit-margin">
      <div className="label">EBIT Margin (%)</div>
      <div className="value">
        {company.ebitPercentage ? <>{company.ebitPercentage}</> : PUNCTUATION.EMPTY_VALUE}
      </div>
    </div>
  </>
);

ComparableCompanyExtendedFinancialCols.propTypes = {
  company: PropTypes.object.isRequired
};

/**
 * ComparableCompanyListItem - Individual company card in comparable companies list
 *
 * Displays a single company with all financial metrics, scoring badge,
 * and action buttons (view details, watchlist).
 *
 * Uses MobX observer to react to profilerProjectStore.watchlist and
 * profilerProjectStore.companiesInWatchlistAction changes.
 */
const ComparableCompanyListItem = observer(
  ({
    company,
    layout,
    project,
    onCompanyDetailsClick,
    onWatchlistClick,
    profilerAccessStore,
    profilerProjectStore,
    utilsStore
  }) => {
    // Check if company is in watchlist
    const isInWatchlist = useMemo(
      () => profilerProjectStore.watchlist.findIndex((_c) => _c.id === company.id) !== -1,
      [profilerProjectStore.watchlist, company.id]
    );

    // Watchlist button configuration
    const canViewWatchlist = profilerAccessStore.canViewProfilerWatchlist;
    const watchlistButtonTooltipContent = getWatchlistTooltipContent(
      canViewWatchlist,
      isInWatchlist
    );
    const watchlistButtonTooltipPosition = getWatchlistTooltipPosition(
      canViewWatchlist,
      layout,
      isInWatchlist
    );
    const watchlistButtonTooltipClickable = !canViewWatchlist;

    const handleWatchlistClick = () => {
      onWatchlistClick(company);
    };

    const openDetailsInNewTab = () =>
      utilsStore.openCompanyDetails(company.id, { ref: company.name });

    // Cmd/Ctrl-click opens a new tab; a plain click opens details in place. The
    // right-click "Open in new tab" menu shares the same openDetailsInNewTab thunk.
    const handleCompanyDetailsClick = openInNewTabOnModifier(openDetailsInNewTab, () =>
      onCompanyDetailsClick(company)
    );

    // Generate stable tooltip IDs
    const nameTooltipId = useMemo(() => getTooltipId(), []);
    const descriptionTooltipId = useMemo(() => getTooltipId(), []);
    const grossProfitTooltipId = useMemo(() => getTooltipId(), []);
    const basicRevenueTooltipId = useMemo(() => getTooltipId(), []);
    const basicEmployeesTooltipId = useMemo(() => getTooltipId(), []);

    const hasExtendedAccess = profilerAccessStore.canViewProfilerDetailsExtendedData(project);
    // TD-2499: CAGR 3Y is visible to Lite (details access); EBIT + EBIT-margin stay Full-only.
    const hasDetailsAccess = profilerAccessStore.canViewProfilerDetails(project);

    return (
      <div
        key={company.id}
        className="item"
        style={getComparableCompanyItemStyle(layout, hasExtendedAccess, hasDetailsAccess)}
      >
        {/* Scoring Badge - Desktop */}
        {company.showScoring && layout === 'desktop' && (
          <>
            <div
              className="result-scoring-badge noselect"
              id={`result-scoring-badge-${company.id}`}
              style={{ backgroundColor: company.scoreSecondaryColor }}
            >
              <div className="score-icon-wrap">
                <img
                  src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                  alt=""
                  style={{ filter: company.scoreIconFilter }}
                />
              </div>
              <div className="score-label" style={{ color: company.scorePrimaryColor }}>
                {company.scoreLabel}
              </div>
            </div>
            <Tooltip
              anchorSelect={`#result-scoring-badge-${company.id}`}
              multiline={true}
              variant="light"
              place="bottom-end"
              opacity={1}
              content={company.scoreTooltipText}
              className={company.scoreTooltipClassName}
              clickable
            />
          </>
        )}

        {/* Company Name - Desktop */}
        <div className="col company-name desktop-only">
          <div className="label">Company name</div>
          <div className="value">
            <span
              id={nameTooltipId}
              className="company-name-link"
              role="button"
              tabIndex={0}
              onClick={handleCompanyDetailsClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCompanyDetailsClick(company);
                }
              }}
            >
              {company.name || PUNCTUATION.EMPTY_VALUE}
            </span>
            <Tooltip
              anchorSelect={`#${nameTooltipId}`}
              multiline={true}
              variant="dark"
              place={utilsStore.windowWidth > 1200 ? 'bottom-center' : 'bottom-end'}
              opacity={1}
              content={'View details'}
              className={'company-name-view-details-tooltip'}
            />
          </div>
        </div>

        {/* Company Name - Mobile */}
        <div className="company-name-mobile mobile-only">
          <div className="left">
            <div className="c-name">{company.name || PUNCTUATION.EMPTY_VALUE}</div>
          </div>
          <div className="right">
            {company.showScoring && layout === 'mobile' && (
              <>
                <div
                  className="result-scoring-badge noselect"
                  id={`result-scoring-badge-${company.id}`}
                  style={{ backgroundColor: company.scoreSecondaryColor }}
                >
                  <div className="score-icon-wrap">
                    <img
                      src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                      alt=""
                      style={{ filter: company.scoreIconFilter }}
                    />
                  </div>
                  <div className="score-label" style={{ color: company.scorePrimaryColor }}>
                    {company.scoreLabel}
                  </div>
                </div>
                <Tooltip
                  anchorSelect={`#result-scoring-badge-${company.id}`}
                  multiline={true}
                  variant="light"
                  place={utilsStore.windowWidth > 1200 ? 'bottom-center' : 'bottom-end'}
                  opacity={1}
                  content={company.scoreTooltipText}
                  className={company.scoreTooltipClassName}
                  clickable
                />
              </>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="col headquarters">
          <div className="label">Location</div>
          <div className="value">{company.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
        </div>

        {/* Description */}
        <div className="col description">
          {layout === 'desktop' && !!company.description && (
            <>
              <div
                id={descriptionTooltipId}
                className="default-black-info-circle-wrap description-similar-companies-button"
              >
                <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
              </div>
              <Tooltip
                anchorSelect={`#${descriptionTooltipId}`}
                multiline={true}
                variant="light"
                place="bottom-center"
                opacity={1}
                content={company.description}
                className={'description-similar-companies-tooltip'}
                clickable
              />
            </>
          )}
          <div className="label">Description</div>
          <div className="value">{company.description || PUNCTUATION.EMPTY_VALUE}</div>
        </div>

        {/* Revenue */}
        <div className="col revenue">
          <div className="label">Sales (€M)</div>
          <div className="value">
            <ComparableCompanyRevenueValue
              company={company}
              grossProfitTooltipId={grossProfitTooltipId}
              basicRevenueTooltipId={basicRevenueTooltipId}
            />
          </div>
        </div>

        {/* CAGR 3Y - Details Access (Lite + Full) */}
        {hasDetailsAccess && (
          <div className="col revenue-yoy">
            <div className="label">CAGR 3Y (%)</div>
            <div className="value">
              {company.cagr3Y ? <>{company.cagr3Y}</> : PUNCTUATION.EMPTY_VALUE}
            </div>
          </div>
        )}

        {/* EBIT + EBIT Margin - Extended Access Only */}
        {hasExtendedAccess && <ComparableCompanyExtendedFinancialCols company={company} />}

        {/* Employees */}
        <div className="col employees no-border">
          <div className="label">Employees</div>
          <div className="value">
            <ComparableCompanyEmployeesValue
              company={company}
              basicEmployeesTooltipId={basicEmployeesTooltipId}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="col actions">
          {layout === 'desktop' && (
            <>
              <IconButton
                wrapClassName="view-details-comparable-companies"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                onClick={handleCompanyDetailsClick}
                tooltipText="View details"
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(company.id, { ref: company.name });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="watchlist-action-btn-comparable-companies"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.WATCHLIST}
                disabled={
                  profilerProjectStore.isLoadingWatchlist ||
                  profilerProjectStore.initialLoadWatchlist ||
                  !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
                  !profilerAccessStore.canViewProfilerWatchlist
                }
                tooltipContent={watchlistButtonTooltipContent}
                tooltipPosition={watchlistButtonTooltipPosition}
                tooltipClickable={watchlistButtonTooltipClickable}
                filled={isInWatchlist}
                onClick={handleWatchlistClick}
              />
            </>
          )}
          {layout === 'mobile' && (
            <div className="actions-stretch">
              <IconButton
                wrapClassName="view-details-comparable-companies-mobile"
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                onClick={handleCompanyDetailsClick}
                innerText="View details"
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(company.id, { ref: company.name });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="watchlist-action-btn-comparable-companies-mobile"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.WATCHLIST}
                disabled={
                  profilerProjectStore.isLoadingWatchlist ||
                  profilerProjectStore.initialLoadWatchlist ||
                  !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
                  !profilerAccessStore.canViewProfilerWatchlist
                }
                tooltipContent={watchlistButtonTooltipContent}
                tooltipPosition={watchlistButtonTooltipPosition}
                tooltipClickable={watchlistButtonTooltipClickable}
                filled={isInWatchlist}
                onClick={handleWatchlistClick}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default ComparableCompanyListItem;
